<template>
    <editor-content class="rich-text-editor" :editor="editor" />
</template>

<script setup>
    import { Editor, EditorContent } from "@tiptap/vue-2";
    import StarterKit from "@tiptap/starter-kit";
    import { onMounted, onUnmounted, ref, toRefs, watch } from "vue";
    import isEqual from "lodash/isEqual";
    import Commands from "@/components/Editor/commands";
    import frasSuggestion from "@/components/Editor/frasSuggestion";
    import FrasNode from "@/components/Editor/frasNode";
    import { getFraser } from "@/components/Editor/helpers";

    const props = defineProps({
        content: {
            type: [Object, String],
        },
        /**
         * Ska content hanteras som icke-rich text?
         */
        simpleEditor: {
            type: Boolean,
            required: false,
            default: () => false,
        },
        /**
         * Värden som ska kollas mot för att se om en fras som lagts in i content har ett värde. Om det inte har ett
         * värde kommet ett felmeddelande skrivas ut om att frasen saknar värde
         */
        frasValues: {
            type: Map,
            required: false,
        },
        /**
         * Värden som fraser ska konverteras till och sättas i content. convertFrasToValue måste vara true för att
         * konvertering ska ske. Istället för $enfras kommer $enfras översättas till motsvarande värde
         */
        convertValues: {
            type: Map,
            required: false,
        },
        /**
         * Ska fraser konverteras till dess värde vid inmatning? Istället för $enfras kommer $enfras översättas till
         * motsvarande värde
         */
        convertFrasToValue: {
            type: Boolean,
            required: false,
            default: () => false,
        },
    });

    const { content, simpleEditor, frasValues, convertFrasToValue, convertValues } = toRefs(props);

    const editor = ref();
    // Sparar senaste payload fran editorn for att undvika onodig setContent-loop.
    const lastEmittedContent = ref(null);

    const emit = defineEmits(["input"]);

    function onUpdate({ eventType, editor: overrideEditor }) {
        if (eventType === "update") {
            emitUpdate({ editor: editor.value });

            return;
        }

        if (eventType === "create") {
            emitUpdate({ editor: overrideEditor });
        }
    }

    function emitUpdate({ editor }) {
        if (!editor) {
            return;
        }
        const payload = simpleEditor.value ? editor.getText() : editor.getJSON();

        lastEmittedContent.value = payload;
        emit("input", payload);
    }

    // Gör om sträng till JSON som stödjs av Tiptap vid rendering
    const stringToJson = (text, content, fraser) => {
        const triggerIndex = text.indexOf("$");

        if (text.length <= 0) {
            return content;
        }

        // Om strängen börjar med $
        if (triggerIndex === 0) {
            const foundFras = fraser.find((fras) => text.startsWith(fras));

            // Om det inte är en fras
            if (!foundFras) {
                return stringToJson(
                    text.slice(triggerIndex + 1),
                    [
                        ...content,
                        {
                            type: "text",
                            text: text.slice(0, 1),
                        },
                    ],
                    fraser
                );
            }

            const frasContent = [
                {
                    type: "fras",
                    content: [
                        {
                            type: "text",
                            text: foundFras,
                        },
                    ],
                },
            ];

            // Lägg till whitespace om tecknet efter frasen är en bokstav, så att det läses av korrekt i BE
            if (text.at(foundFras.length)?.match(/\w/u)) {
                frasContent.push({
                    type: "text",
                    text: " ",
                });
            }

            return stringToJson(text.slice(foundFras.length), [...content, ...frasContent], fraser);
            // Om det finns ett $ i texten
        } else if (triggerIndex >= 0) {
            return stringToJson(
                text.slice(triggerIndex),
                [
                    ...content,
                    {
                        type: "text",
                        text: text.slice(0, triggerIndex),
                    },
                ],
                fraser
            );
            // Om det inte finns ett $ i texten (när resten av strängen inte innehåller några fler möjliga fraser)
        } else {
            return stringToJson(
                "",
                [
                    ...content,
                    {
                        type: "text",
                        text,
                    },
                ],
                fraser
            );
        }
    };

    // Konvertera sträng till JSON eller returnera JSON vid rendering
    const checkAndConvertContent = async (sentContent) => {
        if (typeof sentContent !== "string") {
            return sentContent;
        }

        const frasList = await getFraser({});
        const fraser = frasList.map(({ value }) => value);

        return {
            type: "doc",
            content: [
                {
                    type: "paragraph",
                    content: stringToJson(sentContent, [], fraser),
                },
            ],
        };
    };

    defineExpose({
        editor,
    });

    watch([content], async ([newContent]) => {
        if (!editor.value) {
            return;
        }

        // Om parent skickar tillbaka samma payload som editorn nyss emit:ade, hoppa.
        if (isEqual(newContent, lastEmittedContent.value)) {
            return;
        }
        const baseContent = typeof newContent === "string" ? newContent : newContent.content;

        if (baseContent) {
            editor.value?.commands.setContent(await checkAndConvertContent(baseContent), false);
            emitUpdate({ editor: editor.value });
        }
    });

    onMounted(async () => {
        editor.value = new Editor({
            extensions: [
                StarterKit.configure({}),
                FrasNode,
                Commands.configure({
                    frasSuggestion: await frasSuggestion({ simpleEditor: simpleEditor.value }),
                }),
            ],
            content: await checkAndConvertContent(content.value),
            onUpdate: () => onUpdate({ eventType: "update" }),
            onCreate: (props) => {
                if (content.value) {
                    onUpdate({ eventType: "create", editor: props.editor });
                }
            },
            editorProps: {
                handleKeyDown: (event) => {},
            },
        });

        watch(frasValues, () => {
            onUpdate({ eventType: "update" });
        });
    });

    onUnmounted(() => {
        editor.value?.destroy();
    });
</script>

<style scoped lang="scss">
    @import "@/style/variables";

    .rich-text-editor {
        ::v-deep(.fras) {
            background-color: $color-readonly-background;

            &.ProseMirror-selectednode {
                background-color: $color-hover;
            }
        }

        ::v-deep(.ProseMirror) {
            height: 100%;
            padding: 3rem;

            &.ProseMirror-focused {
                border: none;
                outline: none;
            }

            p {
                margin-bottom: 1rem;
            }
        }
    }
</style>
